import { PublicRatingScope } from 'components/ReviewAudit/PublicRatings'
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { connect } from "react-redux"
import { Link, withRouter } from "react-router-dom"
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap"
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
} from "react-bootstrap-table2-paginator"
import ToolkitProvider from "react-bootstrap-table2-toolkit"
import BootstrapTable from "react-bootstrap-table-next"
import { debounce, isEmpty } from "lodash"

import Breadcrumbs from "components/Common/Breadcrumb"
import PastLastPage, { isPastLastPage } from "components/Common/PastLastPage"
import listColumns, { MatchedChildrenStrip, hasChildrenFound } from "./listColumns"
import FilterList from "./FilterList"
import Tenis10Families from "./Tenis10Families"
import { personaFilterLabel } from "./personas"
import { getPlayersList as getPlayers, getPlayersLocations } from "./actions"
import SearchBox from "components/Common/SearchBox"
import "../../../assets/scss/custom/pages/_playerAccounts.scss"
import {
  DEFAULT_PLAYER_LIST_OPTIONS,
  applyPlayersFilterChange,
  applyPlayersSearchChange,
  applyPlayersSortChange,
  getPlayersLocationFilters,
  shouldFetchPlayersList,
} from "./playerListQueryPolicy"

const SECONDARY_FILTERS = new Set(["age", "gender", "membership"])

const filterLabels = {
  sportType: "Sport",
  "gameLevel[singles]": "Singles",
  "gameLevel[doubles]": "Doubles",
  location: "Location",
  status: "Status",
  gender: "Gender",
  membership: "Membership",
  personas: "Persona",
}

const formatFilterValue = ([key, value]) => {
  if (key === "age") {
    const [minimum, maximum] = String(value).split(",")
    const from = minimum === "0" ? "any" : minimum
    const to = maximum === "100" ? "any" : maximum

    return `Age: ${from}–${to}`
  }

  if (key === "personas") return `${filterLabels.personas}: ${personaFilterLabel(value)}`

  return `${filterLabels[key] || key}: ${value}`
}

const Players = props => {
  const { playersList, getPlayers, getPlayersLocations, userDetails } = props
  const players = playersList
  const pageOptions = {
    page: players.page || DEFAULT_PLAYER_LIST_OPTIONS.page,
    sizePerPage: players.limit || DEFAULT_PLAYER_LIST_OPTIONS.limit,
    totalSize: players.totalResults || 0,
    custom: true,
    hidePageListOnlyOnePage: true,
    hideSizePerPage: true,
  }
  const restored = props.location.state?.playerListContext || {}
  const initialOptions = { ...DEFAULT_PLAYER_LIST_OPTIONS, ...restored.options }
  const [advancedFilters, setAdvancedFilters] = useState(Boolean(restored.advancedFilters))
  const [filters, setFilters] = useState(restored.filters || {})
  const [searchInputText, setSearchInputText] = useState(restored.searchInputText || "")
  const [activeSearchText, setActiveSearchText] = useState(restored.activeSearchText || "")
  const [options, setOptions] = useState(initialOptions)
  const playerListFilters = useMemo(
    () =>
      activeSearchText ? { ...filters, search: activeSearchText } : filters,
    [activeSearchText, filters]
  )
  const playersLocationFilters = useMemo(
    () => getPlayersLocationFilters(filters),
    [filters]
  )
  const activeFilterEntries = useMemo(() => Object.entries(filters), [filters])
  const secondaryFilterCount = activeFilterEntries.filter(([key]) =>
    SECONDARY_FILTERS.has(key)
  ).length
  const totalResults = Number(players.totalResults) || 0
  const currentPage = Number(players.page) || options.page
  const pageSize = Number(players.limit) || options.limit
  const firstResult = totalResults ? (currentPage - 1) * pageSize + 1 : 0
  const lastResult = Math.min(currentPage * pageSize, totalResults)
  // The profile breadcrumb restores the list's page, which a Delete or a Block can leave past the end. The answer held
  // is judged on its own page: the next read empties its rows but keeps its page and page count, so "Go to first page",
  // a filter or Rows keep the message until the new answer, and nothing is past the end before the first answer.
  const pastEnd = isPastLastPage(Number(players.page), players)

  useEffect(() => {
    if (!shouldFetchPlayersList(playerListFilters)) return

    getPlayers(playerListFilters, options)
  }, [getPlayers, playerListFilters, options])

  // The Location options group every matching player (seconds on DEV), so they are read when their menu opens, not with
  // the list; options read for an earlier filter set are not offered for the current one.
  const [locationFiltersRead, setLocationFiltersRead] = useState(null)
  const loadLocations = useCallback(() => {
    setLocationFiltersRead(playersLocationFilters)
    getPlayersLocations(playersLocationFilters)
  }, [getPlayersLocations, playersLocationFilters])

  const handleFiltersChange = useCallback(
    nextFilters => {
      const nextState = applyPlayersFilterChange({
        nextFilters,
        options,
      })

      setFilters(nextState.filters)
      setOptions(nextState.options)
    },
    [options]
  )

  const applySearchQuery = useCallback(
    value => {
      const nextState = applyPlayersSearchChange({
        filters,
        options,
        searchText: value,
      })

      if (!nextState.shouldUpdate) return

      setActiveSearchText(nextState.searchText)
      setSearchInputText(nextState.inputText)
      setOptions(nextState.options)
    },
    [filters, options]
  )

  const handleChangeWithDebounce = useMemo(
    () => debounce(applySearchQuery, 700),
    [applySearchQuery]
  )

  useEffect(
    () => () => handleChangeWithDebounce.cancel(),
    [handleChangeWithDebounce]
  )

  const handleSearchInputChange = useCallback(
    value => {
      setSearchInputText(value || "")
      handleChangeWithDebounce(value || "")
    },
    [handleChangeWithDebounce]
  )

  // the „×”: the whole list at once, without the typing delay
  const clearSearch = useCallback(() => {
    handleChangeWithDebounce.cancel()
    applySearchQuery("")
  }, [handleChangeWithDebounce, applySearchQuery])

  const handleTableChange = (type, data) => {
    switch (type) {
      case "pagination":
        setOptions({
          ...options,
          page: data.page,
        })
        break
      case "search":
        setSearchInputText(data.searchText || "")
        handleChangeWithDebounce(data.searchText || "")
        break
      case "sort":
        const sortOption = `${data.sortField}:${data.sortOrder}`
        const nextState = applyPlayersSortChange({
          options,
          sortBy: sortOption,
        })

        if (nextState.shouldUpdate) setOptions(nextState.options)
        break
      default:
        break
    }
  }

  const resetFilters = () => {
    if (!isEmpty(filters) || activeSearchText || searchInputText) {
      handleChangeWithDebounce.cancel()
      setFilters({})
      setActiveSearchText("")
      setSearchInputText("")
      setOptions({ ...options, page: 1 })
    }
  }

  const removeFilter = key => {
    const nextFilters = { ...filters }
    delete nextFilters[key]
    handleFiltersChange(nextFilters)
  }

  const handlePageSizeChange = event => {
    setOptions({
      ...options,
      page: 1,
      limit: Number(event.target.value),
    })
  }

  const rowClasses = row => [
    row?.status === "blocked" && "disabledRow",
    // P50: the card of a parent with children found closes under the children's row
    hasChildrenFound(row) && "has-children-found",
  ].filter(Boolean).join(" ") || undefined

  // P28 (A2) + P50: the children a search found, in a full-width row under their parent's row, inside the parent's card — always
  // open (no expand column, no toggle on click)
  const rows = players.results || []
  const childrenRows = {
    renderer: row => <MatchedChildrenStrip user={row} />,
    expanded: rows.filter(hasChildrenFound).map(row => row.id),
    nonExpandable: rows.filter(row => !hasChildrenFound(row)).map(row => row.id),
    showExpandColumn: false,
    expandByColumnOnly: true,
    className: "player-children-row",
  }

  return (
    <React.Fragment>
      <PublicRatingScope kind="players" ids={(players?.results || []).map(row => row.userId)}>
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumbs */}
          <Breadcrumbs
            title="User Accounts"
            breadcrumbItem="Users"
            displayTotal={players.totalResults}
          />
          <Row>
            <Col lg="12">
              <Card className="players-directory-card">
                <CardBody className="p-0">
                  <PaginationProvider
                    pagination={paginationFactory(pageOptions)}
                  >
                    {({ paginationProps, paginationTableProps }) => (
                      <ToolkitProvider
                        keyField="id"
                        data={players.results || []}
                        columns={listColumns(userDetails?.userId, { playerListSearch: props.location.search, playerListContext: { filters, options, searchInputText, activeSearchText, advancedFilters } })}
                        bootstrap4
                        search
                      >
                        {toolkitProps => (
                          <React.Fragment>
                            <div className="player-directory-toolbar">
                              <div className="player-directory-heading">
                                <div>
                                  <h4 className="mb-1">User directory</h4>
                                  <p className="text-muted mb-0">
                                    Find an account and open its complete user
                                    profile: player, parent or both.
                                  </p>
                                </div>
                                <Link
                                  to={{ pathname: "/accounts/players/create", state: { playerListSearch: props.location.search,
                                    playerListContext: { filters, options, searchInputText, activeSearchText, advancedFilters } } }}
                                  className="btn btn-success"
                                >
                                  <i className="mdi mdi-plus mr-1" />
                                  Create user
                                </Link>
                              </div>

                              <div className="player-search-row">
                                <SearchBox
                                  look="pill"
                                  className="player-search-box"
                                  onValueChange={handleSearchInputChange}
                                  onClear={clearSearch}
                                  placeholder="Search name, user ID, email or phone"
                                  value={searchInputText}
                                  aria-label="Search users"
                                />
                                <Button
                                  type="button"
                                  color="light"
                                  className="player-more-filters"
                                  onClick={() =>
                                    setAdvancedFilters(value => !value)
                                  }
                                  aria-expanded={advancedFilters}
                                >
                                  <i className="bx bx-slider-alt mr-1" />
                                  More filters
                                  {secondaryFilterCount > 0 && (
                                    <span className="badge badge-primary ml-2">
                                      {secondaryFilterCount}
                                    </span>
                                  )}
                                  <i
                                    className={`bx bx-chevron-${
                                      advancedFilters ? "up" : "down"
                                    } ml-1`}
                                  />
                                </Button>
                              </div>

                              <FilterList
                                advancedFilters={advancedFilters}
                                filters={filters}
                                setFilters={handleFiltersChange}
                                locationsCurrent={locationFiltersRead === playersLocationFilters}
                                onLocationMenuOpen={loadLocations}
                              />

                              {activeFilterEntries.length > 0 && (
                                <div className="player-active-filters">
                                  <span className="text-muted font-size-12">
                                    Applied filters
                                  </span>
                                  {activeFilterEntries.map(entry => (
                                    <button
                                      type="button"
                                      className="player-filter-chip"
                                      key={entry[0]}
                                      onClick={() => removeFilter(entry[0])}
                                      aria-label={`Remove ${formatFilterValue(
                                        entry
                                      )} filter`}
                                    >
                                      {formatFilterValue(entry)}
                                      <i
                                        className="bx bx-x"
                                        aria-hidden="true"
                                      />
                                    </button>
                                  ))}
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm player-clear-filters"
                                    onClick={resetFilters}
                                  >
                                    Clear all
                                  </button>
                                </div>
                              )}
                            </div>
                            <Row className="mx-0">
                              <Col xl="12" className="px-0">
                                <div className="table-responsive player-directory-table">
                                  <BootstrapTable
                                    responsive
                                    remote
                                    bordered={false}
                                    striped={false}
                                    rowClasses={rowClasses}
                                    classes="table align-middle table-hover player-directory-records"
                                    headerWrapperClasses={"thead-light"}
                                    {...toolkitProps.baseProps}
                                    onTableChange={handleTableChange}
                                    {...paginationTableProps}
                                    expandRow={childrenRows}
                                    noDataIndication={pastEnd
                                      ? () => <PastLastPage onFirstPage={() => setOptions({ ...options, page: 1 })} />
                                      : "No users match the current filters."}
                                  />
                                </div>
                              </Col>
                            </Row>
                            <div className="player-directory-footer">
                              <div className="player-result-summary">
                                {!pastEnd && <>
                                  Showing{" "}
                                  <strong>
                                    {firstResult}–{lastResult}
                                  </strong>{" "}
                                  of{" "}
                                </>}
                                <strong>{totalResults.toLocaleString()}</strong>{" "}
                                users
                              </div>
                              {!pastEnd && <div className="pagination pagination-rounded inner-custom-pagination mb-0">
                                <PaginationListStandalone
                                  {...paginationProps}
                                />
                              </div>}
                              <label className="player-page-size mb-0">
                                <span>Rows</span>
                                <select
                                  className="custom-select custom-select-sm"
                                  value={options.limit}
                                  onChange={handlePageSizeChange}
                                >
                                  {[15, 30, 50].map(size => (
                                    <option value={size} key={size}>
                                      {size}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            </div>
                            {/* RELEASE-TENIS10 § 11 P77: the tenis10.ro families the search names too, under the accounts */}
                            <Tenis10Families search={activeSearchText} accountsFound={totalResults}
                              onSearch={value => { handleChangeWithDebounce.cancel(); applySearchQuery(value) }}
                              listState={{ playerListSearch: props.location.search, playerListContext: { filters, options, searchInputText, activeSearchText, advancedFilters } }} />
                          </React.Fragment>
                        )}
                      </ToolkitProvider>
                    )}
                  </PaginationProvider>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      </PublicRatingScope>
    </React.Fragment>
  )
}

const mapStateToProps = ({
  session: { userDetails } = {},
  players: { playersList } = {},
}) => ({
  playersList,
  userDetails,
})

const mapDispatchToProps = dispatch => ({
  getPlayers: (filters, options) => dispatch(getPlayers(filters, options)),
  getPlayersLocations: filters => dispatch(getPlayersLocations(filters)),
})

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Players))
